import { useEffect, useState } from "react";
import { fetchPublicJson } from "./publicContent";

export interface FAQItem {
  id?: string;
  question: string;
  answer: string;
  link?: string | null;
  active?: boolean;
}

export type FAQLoadState =
  | {
      status: "loading";
      faqs: FAQItem[];
    }
  | {
      status: "ready";
      faqs: FAQItem[];
    }
  | {
      status: "unavailable";
      faqs: FAQItem[];
    };

const FALLBACK_LANGUAGE = "en";

function toBool(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

export function normalizeContentLanguage(language?: string | null): string {
  const normalized = (language ?? "").trim().toLowerCase();
  if (!normalized) {
    return FALLBACK_LANGUAGE;
  }

  const [baseLanguage] = normalized.split(/[-_]/);
  return baseLanguage || FALLBACK_LANGUAGE;
}

export function normalizeFaqLanguage(language?: string | null): string {
  return normalizeContentLanguage(language);
}

function normalizeFaqItem(raw: unknown): FAQItem | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const question =
    typeof record.question === "string" ? record.question.trim() : "";
  const answer = typeof record.answer === "string" ? record.answer.trim() : "";

  if (!question || !answer) {
    return null;
  }

  const id =
    typeof record.id === "string" && record.id.trim()
      ? record.id.trim()
      : undefined;
  const link =
    typeof record.link === "string" && record.link.trim()
      ? record.link.trim()
      : null;

  return {
    id,
    question,
    answer,
    link,
    active: toBool(record.active),
  };
}

function normalizeFaqItems(raw: unknown): FAQItem[] {
  const items = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)
      ? ((raw as { items: unknown[] }).items)
      : null;

  if (!items) {
    return [];
  }

  return items
    .map(normalizeFaqItem)
    .filter((item): item is FAQItem => item !== null);
}

export async function loadLocalizedContent<T>(
  collection: string,
  language: string,
  normalize: (raw: unknown) => T[],
  signal?: AbortSignal,
): Promise<T[]> {
  const normalizedLanguage = normalizeContentLanguage(language);
  const payload = await fetchPublicJson<unknown>(
    `${collection}/${normalizedLanguage}.json`,
    signal,
  );
  return normalize(payload);
}

export async function loadLocalizedContentWithFallback<T>(
  collection: string,
  normalize: (raw: unknown) => T[],
  language?: string | null,
  signal?: AbortSignal,
): Promise<T[]> {
  const normalizedLanguage = normalizeContentLanguage(language);

  try {
    return await loadLocalizedContent(collection, normalizedLanguage, normalize, signal);
  } catch (primaryError) {
    if (normalizedLanguage === FALLBACK_LANGUAGE) {
      throw primaryError;
    }
  }

  return loadLocalizedContent(collection, FALLBACK_LANGUAGE, normalize, signal);
}

export async function loadFaqItems(
  language: string,
  signal?: AbortSignal,
): Promise<FAQItem[]> {
  return loadLocalizedContent("faqs", language, normalizeFaqItems, signal);
}

export async function loadFaqItemsWithFallback(
  language: string,
  signal?: AbortSignal,
): Promise<FAQItem[]> {
  return loadLocalizedContentWithFallback(
    "faqs",
    normalizeFaqItems,
    language,
    signal,
  );
}

export function useFaqItems(language?: string | null): FAQLoadState {
  const normalizedLanguage = normalizeFaqLanguage(language);
  const [state, setState] = useState<FAQLoadState>({
    status: "loading",
    faqs: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({
      status: "loading",
      faqs: [],
    });

    const run = async () => {
      try {
        const faqs = await loadFaqItemsWithFallback(
          normalizedLanguage,
          controller.signal,
        );

        if (!active || controller.signal.aborted) {
          return;
        }

        setState({
          status: "ready",
          faqs,
        });
      } catch (error) {
        if (!active || controller.signal.aborted) {
          return;
        }

        setState({
          status: "unavailable",
          faqs: [],
        });
      }
    };

    void run();

    return () => {
      active = false;
      controller.abort();
    };
  }, [normalizedLanguage]);

  return state;
}
